import axios from 'axios';
import { AuthService } from './AuthService';
import { AzureAuthData } from 'types/Azure';
import { BASE_URL } from 'config/api';

// Google auth endpoint is handled server-side via `/google-auth`
const AZURE_BASE_URL = BASE_URL;

class GoogleAuthService {
  private static instance: GoogleAuthService;
  private googleAuthData: AzureAuthData | undefined = undefined;
  private googleAuthCheckPromise: Promise<AzureAuthData | null> | null = null;

  private constructor() {}

  public static getInstance(): GoogleAuthService {
    if (!GoogleAuthService.instance) {
      GoogleAuthService.instance = new GoogleAuthService();
    }
    return GoogleAuthService.instance;
  }

  public async checkGoogleAuth(): Promise<AzureAuthData | null> {
    // If we already have a health check in progress, return that promise
    if (this.googleAuthCheckPromise) {
      return this.googleAuthCheckPromise;
    }

    // If we already have cached health data, return it immediately
    if (this.googleAuthData) {
      return Promise.resolve(this.googleAuthData);
    }

    // Start a new health check
    this.googleAuthCheckPromise = this.performGoogleAuthCheck();

    try {
      const result = await this.googleAuthCheckPromise;
      this.googleAuthData = result ?? undefined;
      return result;
    } finally {
      this.googleAuthCheckPromise = null;
    }
  }

  private async performGoogleAuthCheck(): Promise<AzureAuthData | null> {
    try {
      console.log('🏥 [GOOGLE AUTH] Checking Google authentication...');

      const response = await axios.get(`${AZURE_BASE_URL}/google-auth`, {
        headers: {
          accept: 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });

      const googleAuthData: AzureAuthData = response.data;

      console.log('✅ [GOOGLE AUTH] Google authentication successful:', {
        access_token: googleAuthData.access_token,
        token_type: googleAuthData.token_type,
        expires_in: googleAuthData.expires_in,
      });

      await AuthService.handleAccessToken(googleAuthData.access_token, 'google');
      return googleAuthData;
    } catch (error: any) {
      console.error('❌ [GOOGLE AUTH] Google authentication failed:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
      });

      // If EasyAuth is configured but the local user/header isn't available,
      // the backend returns 403. We interpret that as "provider is enabled".
      if (error.response?.status === 403) {
        return {
          access_token: '',
          token_type: '',
          expires_in: 0,
          is_enabled: true,
        } as unknown as AzureAuthData;
      }

      return null;
    }
  }

  /**
   * Re-run the exchange to mint a fresh token for an already-signed-in user.
   * See `AzureAuthService.forceRenew` — same contract, same empty-token trap in
   * the 403 branch.
   *
   * Note the asymmetry with Azure: `AzureAuthProvider` probes on every mount,
   * so an Azure session already re-exchanges on a full page reload. Google has
   * no such provider and only re-exchanges when `LoginForm` probes it, which
   * makes this the *only* renewal path for a Google session in a long-lived SPA.
   */
  public async forceRenew(): Promise<boolean> {
    this.googleAuthData = undefined;
    try {
      const data = await this.performGoogleAuthCheck();
      if (!data?.access_token) return false;
      this.googleAuthData = data;
      return true;
    } catch {
      return false;
    }
  }

  public initiateGoogleLogin(): void {
    console.log('🔐 [GOOGLE AUTH] Initiating Google login...');
    const loginUrl = `${AZURE_BASE_URL}/.auth/login/google?post_login_redirect_uri=/`;
    window.location.href = loginUrl;
  }

  public logoutFromGoogle(): void {
    console.log('🚪 [GOOGLE AUTH] Logging out from Google...');
    this.googleAuthData = undefined;
    // Use EasyAuth platform logout so we sign out from all configured providers.
    const logoutUrl = `${AZURE_BASE_URL}/.auth/logout?post_logout_redirect_uri=/`;
    window.location.href = logoutUrl;
  }
}

export default GoogleAuthService.getInstance();

